<template>
  <div class="bill-detail-page m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#3F033B"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#3F033B" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Top Navigation & Actions Bar -->
    <v-layout wrap align-center justify-space-between class="bill-header-bar mb-4">
      <v-flex xs12 sm8 text-left class="d-flex align-center">
        <v-btn icon class="mr-2 back-btn" @click="$router.go(-1)" title="Go Back">
          <v-icon color="#3F033B">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="d-flex align-center flex-wrap">
          <span class="mainHeader mr-3">Bill Details</span>
          <span class="bill-no-badge" v-if="billData.billNo">
            #{{ (billData.billPrefix || '') + billData.billNo }}
          </span>
          <span class="status-pill ml-2" :class="getStatusClass(billData.paymentStatus)">
            <span class="status-dot"></span>
            {{ formatStatus(billData.paymentStatus) }}
          </span>
        </div>
      </v-flex>

      <v-flex xs12 sm4 text-right class="mt-3 mt-sm-0">
        <v-btn
          color="#3F033B"
          dark
          class="rounded-lg text-capitalize px-4 print-btn"
          elevation="1"
          @click="downloadReciept()"
        >
          <v-icon left small>mdi-printer</v-icon>
          Print Invoice
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- 3 Overview Cards -->
    <v-layout wrap class="overview-cards-row mb-5">
      <!-- Card 1: Bill / Invoice Info -->
      <v-flex xs12 md4 class="pa-2">
        <v-card class="detail-card" elevation="0">
          <div class="detail-card-header">
            <div class="header-icon-circle purple-icon">
              <v-icon small color="#3F033B">mdi-receipt-text-outline</v-icon>
            </div>
            <span class="card-title">Invoice Information</span>
          </div>

          <div class="card-content">
            <div class="info-row">
              <span class="info-label">Bill Number</span>
              <span class="info-value font-weight-bold" style="color: #3F033B">
                {{ (billData.billPrefix || '') + (billData.billNo || '—') }}
              </span>
            </div>

            <div class="info-row">
              <span class="info-label">Purchase Date</span>
              <span class="info-value">
                <v-icon x-small color="#64748B" class="mr-1">mdi-calendar-blank-outline</v-icon>
                {{ formatDate(billData.create_date) }}
              </span>
            </div>

            <div class="info-row">
              <span class="info-label">Billing Type</span>
              <span class="tax-chip" :class="taxType == 'true' ? 'tax-gst' : 'tax-demo'">
                {{ taxType == 'true' ? 'GST Invoice' : 'Standard / Demo' }}
              </span>
            </div>

            <!-- Handled By (Salesman Profile Card) -->
            <div class="salesman-box">
              <div class="salesman-box-header">
                <div class="d-flex align-center">
                  <v-icon x-small color="#3F033B" class="mr-1">mdi-account-tie</v-icon>
                  <span class="salesman-box-title">Handled By</span>
                </div>
                <span v-if="salesmanInfo.staffCode" class="staff-code-pill">
                  {{ salesmanInfo.staffCode }}
                </span>
              </div>

              <div class="salesman-box-body">
                <div class="salesman-avatar">
                  <span>{{ salesmanInitials }}</span>
                </div>
                <div class="salesman-info-col">
                  <div class="salesman-name">
                    {{ salesmanInfo.name || billData.staffCode || '—' }}
                  </div>
                  <div class="salesman-meta-row" v-if="salesmanInfo.role || salesmanInfo.phone">
                    <span v-if="salesmanInfo.role" class="salesman-role-chip">
                      <v-icon x-small color="#6B21A8" class="mr-0.5">mdi-shield-account-outline</v-icon>
                      {{ salesmanInfo.role }}
                    </span>
                    <span v-if="salesmanInfo.phone" class="salesman-phone-val">
                      <v-icon x-small color="#64748B" class="mr-0.5">mdi-phone-outline</v-icon>
                      <a :href="'tel:' + salesmanInfo.phone" class="phone-link">{{ salesmanInfo.phone }}</a>
                    </span>
                  </div>
                  <div v-if="salesmanInfo.email" class="salesman-email-val">
                    <v-icon x-small color="#64748B" class="mr-0.5">mdi-email-outline</v-icon>
                    <span class="email-text">{{ salesmanInfo.email }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card>
      </v-flex>

      <!-- Card 2: Customer Details -->
      <v-flex xs12 md4 class="pa-2">
        <v-card class="detail-card" elevation="0">
          <div class="detail-card-header">
            <div class="header-icon-circle blue-icon">
              <v-icon small color="#2563EB">mdi-account-outline</v-icon>
            </div>
            <span class="card-title">Customer Details</span>
          </div>

          <div class="card-content">
            <div class="info-row">
              <span class="info-label">Customer Name</span>
              <span class="info-value customer-name-val">
                {{ billData.customerName || '—' }}
              </span>
            </div>

            <div class="info-row">
              <span class="info-label">Phone Number</span>
              <span class="info-value">
                <v-icon x-small color="#64748B" class="mr-1">mdi-phone-outline</v-icon>
                {{ billData.mobileNumber || '—' }}
              </span>
            </div>

            <div class="info-row align-start">
              <span class="info-label">Address</span>
              <span class="info-value address-val text-right">
                <v-icon x-small color="#64748B" class="mr-1">mdi-map-marker-outline</v-icon>
                {{ formattedAddress }}
              </span>
            </div>
          </div>
        </v-card>
      </v-flex>

      <!-- Card 3: Weight & Financial Summary -->
      <v-flex xs12 md4 class="pa-2">
        <v-card class="detail-card summary-card" elevation="0">
          <div class="detail-card-header">
            <div class="header-icon-circle gold-icon">
              <v-icon small color="#B45309">mdi-scale-balance</v-icon>
            </div>
            <span class="card-title">Bill Summary</span>
          </div>

          <div class="card-content">
            <div class="summary-mini-grid mb-3">
              <div class="mini-metric">
                <span class="mini-label">Gross Wt</span>
                <span class="mini-val">{{ formatWeight(billData.totalGrossWeight) }}</span>
              </div>
              <div class="mini-metric">
                <span class="mini-label">Net Wt</span>
                <span class="mini-val">{{ formatWeight(billData.totalNetWeight) }}</span>
              </div>
              <div class="mini-metric">
                <span class="mini-label">Stone Wt</span>
                <span class="mini-val">{{ formatWeight(billData.totalStoneWeight) }}</span>
              </div>
            </div>

            <div class="info-row dense">
              <span class="info-label">Stone Price</span>
              <span class="info-value">₹ {{ formatCurrency(billData.totalStonePrice) }}</span>
            </div>

            <div class="info-row dense" v-if="billData.oldGoldAmount && billData.oldGoldAmount > 0">
              <span class="info-label">Old Gold Exchange</span>
              <span class="info-value text-green">- ₹ {{ formatCurrency(billData.oldGoldAmount) }}</span>
            </div>

            <div class="info-row dense" v-if="billData.returnAmount && billData.returnAmount > 0">
              <span class="info-label">Return Gold Amt</span>
              <span class="info-value text-green">- ₹ {{ formatCurrency(billData.returnAmount) }}</span>
            </div>

            <!-- Grand Total Highlight -->
            <div class="total-highlight-banner mt-3">
              <div class="total-label">Total Payable</div>
              <div class="total-amount">₹ {{ formatCurrency(ItemTotal) }}</div>
            </div>
          </div>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Items Section -->
    <v-layout wrap class="mb-6">
      <v-flex xs12 class="pa-2">
        <v-card class="detail-table-card" elevation="0">
          <div class="section-table-header">
            <div class="d-flex align-center">
              <v-icon color="#3F033B" class="mr-2">mdi-cart-outline</v-icon>
              <span class="section-title">Purchased Items</span>
              <span class="count-badge ml-3" v-if="salesList && salesList.length > 0">
                {{ salesList.length }} {{ salesList.length === 1 ? 'Item' : 'Items' }}
              </span>
            </div>
            <div>
              <v-btn
                small
                outlined
                color="#3F033B"
                class="rounded-lg text-capitalize px-3"
                @click="downloadReciept()"
                title="Print Items Receipt"
              >
                <v-icon left small>mdi-printer</v-icon>
                Print
              </v-btn>
            </div>
          </div>

          <div class="table-responsive-container">
            <table class="refined-detail-table m-stack-table m-stack-grid">
              <thead>
                <tr>
                  <th class="text-center" style="width: 55px">S.No.</th>
                  <th class="text-left" style="min-width: 140px">Code / Item</th>
                  <th class="text-center" style="width: 75px">Count</th>
                  <th class="text-right" style="width: 110px">Item Wt (gm)</th>
                  <th class="text-right" style="width: 110px">Net Wt (gm)</th>
                  <th class="text-right" style="width: 120px">Stone Pr (₹)</th>
                  <th class="text-right" style="width: 110px">Stone Wt (gm)</th>
                  <th class="text-right" style="min-width: 120px">Total (₹)</th>
                  <th class="text-center" style="width: 90px">Type</th>
                </tr>
              </thead>
              <tbody v-if="salesList && salesList.length > 0">
                <tr v-for="(item, i) in salesList" :key="i" class="table-row">
                  <td data-label="S.No." class="text-center text-muted">{{ i + 1 }}</td>
                  <td data-label="Code / Item" class="text-left">
                    <div class="d-flex align-center">
                      <span class="code-badge mr-2" v-if="item.designId && item.designId.code">
                        {{ item.designId.code }}
                      </span>
                      <span class="font-weight-medium" v-if="item.itemId && item.itemId.name">
                        {{ item.itemId.name }}
                      </span>
                      <span v-else-if="!item.designId" class="text-muted">—</span>
                    </div>
                  </td>
                  <td data-label="Count" class="text-center font-weight-medium">{{ item.quantity || 1 }}</td>
                  <td data-label="Item Wt (gm)" class="text-right num-cell">{{ formatWeight(item.itemPerWeight) }}</td>
                  <td data-label="Net Wt (gm)" class="text-right num-cell">{{ formatWeight(item.netWeight) }}</td>
                  <td data-label="Stone Pr (₹)" class="text-right num-cell">
                    {{ item.stonePrice ? '₹ ' + formatCurrency(item.stonePrice) : '—' }}
                  </td>
                  <td data-label="Stone Wt (gm)" class="text-right num-cell">{{ formatWeight(item.stoneWeight) }}</td>
                  <td data-label="Total (₹)" class="text-right num-cell total-cell">
                    ₹ {{ formatCurrency(item.taxType == true ? item.amountWithGst : item.amountWithoutGst) }}
                  </td>
                  <td data-label="Type" class="text-center">
                    <span class="type-pill" :class="item.taxType == true ? 'type-gst' : 'type-demo'">
                      {{ item.taxType == true ? 'GST' : 'Demo' }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="9" class="empty-state-cell">
                    <v-icon color="#CBD5E1" size="36">mdi-package-variant</v-icon>
                    <div class="empty-text">No purchased items recorded</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Payment History Section (if present) -->
    <v-layout wrap class="mb-6" v-if="paymentList && paymentList.length > 0">
      <v-flex xs12 class="pa-2">
        <v-card class="detail-table-card" elevation="0">
          <div class="section-table-header">
            <div class="d-flex align-center">
              <v-icon color="#3F033B" class="mr-2">mdi-cash-multiple</v-icon>
              <span class="section-title">Payment History</span>
              <span class="count-badge ml-3">
                {{ paymentList.length }} {{ paymentList.length === 1 ? 'Payment' : 'Payments' }}
              </span>
            </div>
          </div>

          <div class="table-responsive-container">
            <table class="refined-detail-table m-stack-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 55px">S.No.</th>
                  <th class="text-left">Date</th>
                  <th class="text-left">Tender Type</th>
                  <th class="text-right">Amount Paid (₹)</th>
                  <th class="text-right">Balance After (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(payment, i) in paymentList" :key="i" class="table-row">
                  <td data-label="S.No." class="text-center text-muted">{{ i + 1 }}</td>
                  <td data-label="Date" class="text-left">{{ formatDate(payment.create_date) }}</td>
                  <td data-label="Tender Type" class="text-left">
                    {{ payment.paymentType && payment.paymentType.length ? payment.paymentType.map((p) => p.paytype).join(', ') : '—' }}
                  </td>
                  <td data-label="Amount Paid (₹)" class="text-right num-cell total-cell">₹ {{ formatCurrency(payment.payAmount) }}</td>
                  <td data-label="Balance After (₹)" class="text-right num-cell">₹ {{ formatCurrency(payment.billPendingAmount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Return Gold Section (if present) -->
    <v-layout wrap class="mb-6" v-if="returnList && returnList.length > 0">
      <v-flex xs12 class="pa-2">
        <v-card class="detail-table-card" elevation="0">
          <div class="section-table-header">
            <div class="d-flex align-center flex-wrap">
              <v-icon color="#B45309" class="mr-2">mdi-keyboard-return</v-icon>
              <span class="section-title">Return Gold Details</span>
              <span class="code-badge ml-3" v-if="returnBillNo">
                Bill No: {{ returnBillNo }}
              </span>
            </div>
            <div>
              <v-btn
                small
                outlined
                color="#3F033B"
                class="rounded-lg text-capitalize px-3"
                @click="downloadReturnReciept()"
                title="Print Return Voucher"
              >
                <v-icon left small>mdi-printer</v-icon>
                Print Voucher
              </v-btn>
            </div>
          </div>

          <div class="table-responsive-container">
            <table class="refined-detail-table m-stack-table m-stack-grid">
              <thead>
                <tr>
                  <th class="text-left">Item</th>
                  <th class="text-center">HSN</th>
                  <th class="text-center">Qty</th>
                  <th class="text-right">Gr. Wt (gm)</th>
                  <th class="text-right">St. Wt (gm)</th>
                  <th class="text-right">Net Wt (gm)</th>
                  <th class="text-right">St. Pr (₹)</th>
                  <th class="text-right">Making Chg (₹)</th>
                  <th class="text-right">VA (%)</th>
                  <th class="text-right">Discount (₹)</th>
                  <th class="text-right">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ret, idx) in returnList" :key="idx" class="table-row">
                  <td data-label="Item" class="text-left font-weight-medium">
                    {{ (ret.designId ? ret.designId.name : '') + (ret.itemId ? ' - ' + ret.itemId.name : '') || '—' }}
                  </td>
                  <td data-label="HSN" class="text-center text-muted">
                    {{ ret.designId && ret.designId.HSN ? ret.designId.HSN.hsnCode : '—' }}
                  </td>
                  <td data-label="Qty" class="text-center font-weight-medium">{{ ret.quantity || 1 }}</td>
                  <td data-label="Gr. Wt (gm)" class="text-right num-cell">{{ formatWeight(ret.grossWeight) }}</td>
                  <td data-label="St. Wt (gm)" class="text-right num-cell">{{ formatWeight(ret.stonWeight) }}</td>
                  <td data-label="Net Wt (gm)" class="text-right num-cell">{{ formatWeight(ret.netWeight) }}</td>
                  <td data-label="St. Pr (₹)" class="text-right num-cell">{{ ret.stonePrice ? '₹ ' + formatCurrency(ret.stonePrice) : '0.00' }}</td>
                  <td data-label="Making Chg (₹)" class="text-right num-cell">{{ ret.makingCharge ? '₹ ' + formatCurrency(ret.makingCharge) : '0.00' }}</td>
                  <td data-label="VA (%)" class="text-right num-cell">{{ ret.value3Addition ? ret.value3Addition.toFixed(2) + '%' : '0.00%' }}</td>
                  <td data-label="Discount (₹)" class="text-right num-cell">{{ ret.discountAmount ? '₹ ' + formatCurrency(ret.discountAmount) : '0.00' }}</td>
                  <td data-label="Total (₹)" class="text-right num-cell total-cell">
                    ₹ {{ formatCurrency(ret.itemTotalAmount) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Old Gold Section (if present) -->
    <v-layout wrap class="mb-6" v-if="oldgoldList && oldgoldList.length > 0">
      <v-flex xs12 class="pa-2">
        <v-card class="detail-table-card" elevation="0">
          <div class="section-table-header">
            <div class="d-flex align-center">
              <v-icon color="#D97706" class="mr-2">mdi-swap-horizontal</v-icon>
              <span class="section-title">Old Metal Exchange Details</span>
              <span class="count-badge ml-3">
                {{ oldgoldList.length }} {{ oldgoldList.length === 1 ? 'Item' : 'Items' }}
              </span>
            </div>
            <div>
              <v-btn
                small
                outlined
                color="#3F033B"
                class="rounded-lg text-capitalize px-3"
                @click="downloadOldGoldReciept()"
                title="Print Old Gold Invoice"
              >
                <v-icon left small>mdi-printer</v-icon>
                Print Old Gold Invoice
              </v-btn>
            </div>
          </div>

          <div class="table-responsive-container">
            <table class="refined-detail-table m-stack-table m-stack-grid">
              <thead>
                <tr>
                  <th class="text-center" style="width: 55px">S.No.</th>
                  <th class="text-left">Item Name</th>
                  <th class="text-center">Metal</th>
                  <th class="text-right">Rate (₹)</th>
                  <th class="text-center">Qty</th>
                  <th class="text-right">Gr. Wt (gm)</th>
                  <th class="text-right">St. Wt (gm)</th>
                  <th class="text-right">Net Wt (gm)</th>
                  <th class="text-right">Melt (%)</th>
                  <th class="text-right">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(og, idx) in oldgoldList" :key="idx" class="table-row">
                  <td data-label="S.No." class="text-center text-muted">{{ idx + 1 }}</td>
                  <td data-label="Item Name" class="text-left font-weight-medium">{{ og.itemName || '—' }}</td>
                  <td data-label="Metal" class="text-center">{{ og.metalType || 'Gold' }}</td>
                  <td data-label="Rate (₹)" class="text-right num-cell">{{ og.goldRate ? '₹ ' + formatNumber(og.goldRate) : '—' }}</td>
                  <td data-label="Qty" class="text-center font-weight-medium">{{ og.quantity || 1 }}</td>
                  <td data-label="Gr. Wt (gm)" class="text-right num-cell">{{ formatWeight(og.grossWeight) }}</td>
                  <td data-label="St. Wt (gm)" class="text-right num-cell">{{ formatWeight(og.stoneWeight) }}</td>
                  <td data-label="Net Wt (gm)" class="text-right num-cell">{{ formatWeight(og.netWeight) }}</td>
                  <td data-label="Melt (%)" class="text-right num-cell">{{ og.melt ? og.melt + '%' : '0%' }}</td>
                  <td data-label="Amount (₹)" class="text-right num-cell total-cell">
                    ₹ {{ formatCurrency(og.itemAmount) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      salesList: [],
      returnList: [],
      oldgoldList: [],
      paymentList: [],
      billData: {},
      salesmanData: null,
      returnBillNo: "",
      taxType: localStorage.getItem("GSTstatus"),
      ItemTotal: 0,
    };
  },
  mounted() {
    this.getSales();
    this.getReturns();
    this.getOldGold();
  },

  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    formattedAddress() {
      if (!this.billData || !this.billData.customerId) return "—";
      const cust = this.billData.customerId;
      const parts = [];
      if (cust.address && typeof cust.address === "string" && cust.address.trim()) {
        parts.push(cust.address.trim());
      }
      if (
        cust.city &&
        typeof cust.city === "string" &&
        cust.city.trim() &&
        (!cust.address || !cust.address.toLowerCase().includes(cust.city.toLowerCase().trim()))
      ) {
        parts.push(cust.city.trim());
      }
      if (cust.pincode) {
        parts.push(cust.pincode);
      }
      return parts.length > 0 ? parts.join(", ") : "—";
    },
    salesmanInfo() {
      if (this.salesmanData) {
        return this.salesmanData;
      }
      if (this.billData && this.billData.salesman) {
        return this.billData.salesman;
      }
      return {
        name: this.billData.staffCode || "—",
        staffCode: this.billData.staffCode || "",
        role: "",
        phone: "",
        email: "",
      };
    },
    salesmanInitials() {
      const name = (this.salesmanInfo && this.salesmanInfo.name) || "";
      if (!name || name === "—") return "S";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
  },
  methods: {
    getSales() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/individual/bill/itemdetails",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          saleId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.salesList = response.data.data || [];
            this.paymentList = response.data.payments || [];
            this.billData = response.data.saledata || {};
            this.salesmanData =
              response.data.salesman ||
              (response.data.saledata && response.data.saledata.salesman) ||
              null;
            var total =
              this.taxType == "true"
                ? response.data.saledata.totalAmountWithGst
                : response.data.saledata.totalAmountWithoutGst;
            if (!total && response.data.saledata.payableAmount) {
              total = response.data.saledata.payableAmount;
            }
            this.ItemTotal = total || 0;
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },

    getReturns() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/individual/bill/returnitemdetails",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          saleId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.returnList = response.data.returnitems || [];
            if (response.data.data) {
              this.returnBillNo =
                (response.data.data.billPrefix || "") + (response.data.data.billNo || "");
            }
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },

    getOldGold() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/individual/bill/oldgolddetails",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          saleId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.oldgoldList = response.data.oldgolditems || [];
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },

    downloadReciept() {
      const billId =
        this.$route.query.id ||
        this.$route.query.no ||
        (this.billData && this.billData._id);
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/invoice",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: billId,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const fileName = `Invoice_${this.billData.billPrefix || ""}${this.billData.billNo || "bill"}.pdf`;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
          this.msg = "Failed to download invoice";
          this.showsnackbar = true;
        });
    },

    downloadOldGoldReciept() {
      const billId =
        this.$route.query.id ||
        this.$route.query.no ||
        (this.billData && this.billData._id);
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/purchaseinvoice",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: billId,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const fileName = `OldGold_${this.billData.billPrefix || ""}${this.billData.billNo || "bill"}.pdf`;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
          this.msg = "Failed to download old gold invoice";
          this.showsnackbar = true;
        });
    },

    downloadReturnReciept() {
      const billId =
        this.$route.query.id ||
        this.$route.query.no ||
        (this.billData && this.billData._id);
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/voucher",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: billId,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const fileName = `ReturnVoucher_${this.returnBillNo || "voucher"}.pdf`;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
          this.msg = "Failed to download return voucher";
          this.showsnackbar = true;
        });
    },

    formatDate(item) {
      if (!item) return "—";
      var dt = new Date(item);
      if (isNaN(dt.getTime())) return "—";
      var day = dt.getDate();
      var year = dt.getFullYear();
      var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      var month = months[dt.getMonth()];
      return (day < 10 ? "0" + day : day) + " " + month + " " + year;
    },

    formatWeight(val) {
      if (val === null || val === undefined || val === "" || isNaN(val) || Number(val) === 0) return "—";
      return Number(val).toFixed(3) + " g";
    },

    formatCurrency(val) {
      if (val === null || val === undefined || val === "" || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatNumber(val) {
      if (val === null || val === undefined || val === "" || isNaN(val)) return "0";
      return Number(val).toLocaleString("en-IN");
    },

    formatStatus(status) {
      if (!status || status === 0 || status === "0") return "Paid";
      return String(status).trim();
    },

    getStatusClass(status) {
      if (!status) return "status-paid";
      const s = String(status).trim().toLowerCase();
      if (s === "paid" || s === "completed" || s === "success") return "status-paid";
      if (s === "pending" || s === "due" || s === "unpaid" || s === "0") return "status-pending";
      if (s === "partial" || s === "partially paid") return "status-partial";
      return "status-default";
    },
  },
};
</script>

<style scoped>
.bill-detail-page {
  padding-bottom: 32px;
}

/* Header bar */
.bill-header-bar {
  padding-top: 4px;
}

.back-btn {
  background: #ffffff !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 10px !important;
  transition: all 0.2s ease;
}
.back-btn:hover {
  background: #FAF6F0 !important;
  border-color: #3F033B !important;
}

.bill-no-badge {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  font-size: 14px;
  color: #3F033B;
  background: rgba(63, 3, 59, 0.08);
  border: 1px solid rgba(63, 3, 59, 0.16);
  padding: 3px 10px;
  border-radius: 8px;
  letter-spacing: 0.02em;
}

.print-btn {
  font-weight: 600 !important;
  letter-spacing: 0.02em !important;
  background-color: #3F033B !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.print-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(63, 3, 59, 0.25) !important;
}

/* Overview Cards */
.detail-card {
  background: #ffffff !important;
  border-radius: 14px !important;
  border: 1px solid #EAECEF !important;
  box-shadow: 0 4px 16px -2px rgba(63, 3, 59, 0.04), 0 2px 6px -1px rgba(0, 0, 0, 0.02) !important;
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.detail-card:hover {
  box-shadow: 0 6px 20px -2px rgba(63, 3, 59, 0.07), 0 3px 8px -1px rgba(0, 0, 0, 0.04) !important;
}

.detail-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid #F1F5F9;
  margin-bottom: 16px;
}

.header-icon-circle {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.purple-icon {
  background: rgba(63, 3, 59, 0.08);
}
.blue-icon {
  background: #EFF6FF;
}
.gold-icon {
  background: #FEF3C7;
}

.card-title {
  font-size: 14.5px;
  font-weight: 700;
  color: #1E293B;
  letter-spacing: 0.01em;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
}
.info-row.dense {
  font-size: 12.5px;
}
.info-row.align-start {
  align-items: flex-start;
}

.info-label {
  color: #64748B;
  font-weight: 500;
  white-space: nowrap;
}

.info-value {
  color: #1E293B;
  font-weight: 600;
}

.customer-name-val {
  font-size: 14.5px;
  text-transform: capitalize;
  color: #0F172A;
}

.address-val {
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
  line-height: 1.4;
  max-width: 220px;
}

.tax-chip {
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 600;
}
.tax-gst {
  background: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}
.tax-demo {
  background: #F1F5F9;
  color: #475569;
  border: 1px solid #E2E8F0;
}

/* Salesman Info Box */
.salesman-box {
  margin-top: 8px;
  padding: 12px 14px;
  background: #FDF9FC;
  border: 1px solid #F0DAEC;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.2s ease;
}
.salesman-box:hover {
  background: #FCF4FA;
  border-color: #E2BEDC;
}

.salesman-box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 6px;
  border-bottom: 1px dashed #ECD0E6;
}

.salesman-box-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #3F033B;
}

.staff-code-pill {
  font-size: 11px;
  font-weight: 700;
  background: #3F033B;
  color: #FFFFFF;
  padding: 2px 8px;
  border-radius: 6px;
  letter-spacing: 0.03em;
}

.salesman-box-body {
  display: flex;
  align-items: center;
  gap: 12px;
}

.salesman-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3F033B 0%, #631257 100%);
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13.5px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(63, 3, 59, 0.2);
  letter-spacing: 0.02em;
}

.salesman-info-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.salesman-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #0F172A;
  text-transform: capitalize;
  line-height: 1.25;
}

.salesman-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 1px;
}

.salesman-role-chip {
  display: inline-flex;
  align-items: center;
  background: #F3E8FF;
  color: #6B21A8;
  border: 1px solid #E9D5FF;
  border-radius: 4px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 6px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.salesman-phone-val,
.salesman-email-val {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  color: #475569;
}

.phone-link {
  color: #475569;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.15s ease;
}
.phone-link:hover {
  color: #3F033B;
  text-decoration: underline;
}

.email-text {
  color: #64748B;
  font-size: 11.5px;
  word-break: break-all;
}

/* Mini Weight Grid inside Summary Card */
.summary-mini-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 8px;
  text-align: center;
}

.mini-metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mini-label {
  font-size: 10.5px;
  text-transform: uppercase;
  font-weight: 600;
  color: #64748B;
  letter-spacing: 0.04em;
}

.mini-val {
  font-size: 12.5px;
  font-weight: 700;
  color: #1E293B;
  font-variant-numeric: tabular-nums;
}

/* Total Payable Highlight Banner */
.total-highlight-banner {
  margin-top: auto;
  background: #FAF6F0;
  border: 1px solid #EDE4D8;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.total-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #78350F;
}

.total-amount {
  font-size: 19px;
  font-weight: 800;
  color: #3F033B;
  font-variant-numeric: tabular-nums;
}

/* Status Badges */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.status-paid {
  background-color: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}
.status-paid .status-dot {
  background-color: #10B981;
}
.status-pending {
  background-color: #FFFBEB;
  color: #92400E;
  border: 1px solid #FDE68A;
}
.status-pending .status-dot {
  background-color: #F59E0B;
}
.status-partial {
  background-color: #EFF6FF;
  color: #1E40AF;
  border: 1px solid #BFDBFE;
}
.status-partial .status-dot {
  background-color: #3B82F6;
}
.status-default {
  background-color: #F1F5F9;
  color: #475569;
  border: 1px solid #E2E8F0;
}
.status-default .status-dot {
  background-color: #94A3B8;
}

/* Detail Table Card */
.detail-table-card {
  background: #ffffff !important;
  border-radius: 14px !important;
  border: 1px solid #EAECEF !important;
  box-shadow: 0 4px 16px -2px rgba(63, 3, 59, 0.04), 0 2px 6px -1px rgba(0, 0, 0, 0.02) !important;
  overflow: hidden;
}

.section-table-header {
  padding: 16px 20px;
  background: #FAF8F5;
  border-bottom: 2px solid #EDE8E2;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1E293B;
  letter-spacing: 0.01em;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #3F033B;
  background: rgba(63, 3, 59, 0.07);
  border: 1px solid rgba(63, 3, 59, 0.14);
  padding: 2px 8px;
  border-radius: 12px;
}

.code-badge {
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #3F033B;
  background: rgba(63, 3, 59, 0.06);
  border: 1px solid rgba(63, 3, 59, 0.14);
  padding: 2px 7px;
  border-radius: 5px;
  letter-spacing: 0.02em;
}

/* Table */
.table-responsive-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.refined-detail-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.refined-detail-table thead tr {
  background: #F8FAFC;
}

.refined-detail-table th {
  padding: 13px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #555A64;
  white-space: nowrap;
  border-bottom: 1px solid #EAECEF;
}

.refined-detail-table tbody tr.table-row {
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #F1F3F5;
}

.refined-detail-table tbody tr.table-row:hover {
  background-color: #FAF6F0;
}

.refined-detail-table td {
  padding: 14px 16px;
  font-size: 13px;
  color: #1E293B;
  border-bottom: 1px solid #F1F3F5;
  vertical-align: middle;
}

.num-cell {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: #334155;
}

.total-cell {
  font-weight: 700;
  color: #3F033B;
  font-size: 13.5px;
}

.type-pill {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  display: inline-block;
}
.type-gst {
  background: #ECFDF5;
  color: #047857;
  border: 1px solid #A7F3D0;
}
.type-demo {
  background: #F1F5F9;
  color: #475569;
  border: 1px solid #CBD5E1;
}

.empty-state-cell {
  padding: 40px 20px;
  text-align: center;
}
.empty-text {
  font-size: 13px;
  color: #94A3B8;
  margin-top: 8px;
}

.text-muted {
  color: #94A3B8;
}
.text-green {
  color: #059669 !important;
}
</style>